import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControlName,
  FormGroupDirective,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { FormControlTyped, FormGroupTyped } from '@app/../types/typed-forms';
import { NumberServiceType, NumberType } from '@app/core/interfaces/portal/number-evaluator';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { EvaluatedNumber } from '@app/shared/models/evaluated-number';
import { SharedService } from '@app/shared/services/shared.service';
import { FormMapping } from '@app/utils/form-mapping/decorators/form-mapping';
import { FormMappingAttribute } from '@app/utils/form-mapping/decorators/form-mapping-attribute';
import { FormMappingModel } from '@app/utils/form-mapping/models/form-mapping-model';
import { ngValidatorAccessor } from '@app/utils/ng-validator-accessor';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { isBlank, isNotBlank } from '@app/utils/string';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@FormMapping()
class Form extends FormMappingModel {
  @FormMappingAttribute()
  number: string;
}

@UntilDestroy()
@Component({
  selector: 'portal-evaluated-number-input',
  templateUrl: './evaluated-number-input.component.html',
  styles: [],
  providers: [
    ngValueAccessor(EvaluatedNumberInputComponent),
    ngValidatorAccessor(EvaluatedNumberInputComponent),
  ],
  standalone: false,
})
export class EvaluatedNumberInputComponent extends ReactiveComponent implements OnInit, Validator {
  /**
   * Array of number types that are allowed (FIXED_NETWORK, MOBILE, ...). Empty array means all number types are allowed.
   */
  @Input()
  allowedNumberTypes: NumberType[] = [];

  /**
   * Array of number service types that are allowed (CORPORATE, MSISDN, PSTN, VOICEMAIL, ...). Empty array means all service types are allowed.
   */
  @Input()
  allowedServiceTypes: NumberServiceType[] = [];

  /**
   * i18n key for the message that is shown if the evaluated number type is not allowed.
   */
  @Input()
  invalidMessage: string;

  @Input()
  placeholder: string | false = 'numberEvaluator.placeholder';

  @Input('requireValue')
  required: boolean = false;

  evaluatedNumber: EvaluatedNumber | null = null;
  evaluatingNumber: boolean = false;
  form!: FormGroupTyped<Form>;
  id!: string;

  private defaultNumberPattern = /^\d+[/]\d+([/]\d+)?$/;
  private e164Pattern = /^[+](\d{2}\d+)$/;
  private errors: ValidationErrors | null = null;
  private formControl: AbstractControl | null;

  constructor(private sharedService: SharedService) {
    super();
  }

  ngOnInit(): void {
    const controlDirective = this.injector.get(FormControlName);

    if (controlDirective == null) {
      throw new Error('Cannot access parent form control');
    } else {
      this.formControl = this.injector.get(FormGroupDirective).getControl(controlDirective);
      this.id = controlDirective.name as string;
    }

    this.form = Form.formGroup();
    this.form.get('number').setValidators(this.getNumberValidator());
    this.form.get('number').setAsyncValidators(this.getAsyncNumberValidator());

    this.form
      .get('number')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.evaluatedNumber = null;
      });
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.errors;
  }

  writeValue(value: any): void {
    if (this.formControl.pristine) {
      this.form.markAsPristine();
      this.form.get('number').markAsPristine();
    }

    this.form.get('number').setValue(this.format(value));
  }

  private format(value: string): string {
    let formatted = '';

    if (isNotBlank(value)) {
      formatted = '+' + value.replace(/[/+]/g, '');
    }

    return formatted;
  }

  private getNumberValidator(): (control: AbstractControl) => ValidationErrors {
    return (control: FormControlTyped<string>) => {
      let errors: ValidationErrors = {};

      if (this.required && isBlank(control.value)) {
        errors['required'] = true;
      }

      if (control.dirty) {
        if (isNotBlank(control.value)) {
          const isE164 = this.e164Pattern.test(control.value);
          const isSplitNumber = this.defaultNumberPattern.test(control.value);

          if (isE164 === false && isSplitNumber === false) {
            errors['custom'] = 'numberEvaluator.validation.wrongFormat';
          }
        }

        this.onChange(control.value);
      }

      this.updateErrors(errors);

      return errors;
    };
  }

  private stringifyNumber(num: EvaluatedNumber) {
    if (num.ndc?.length == 0) {
      return `${num.cc}/${num.sn}`;
    }
    return `${num.cc}/${num.ndc}/${num.sn}`;
  }

  private getAsyncNumberValidator(): (control: AbstractControl) => Observable<ValidationErrors> {
    return (control: FormControlTyped<string>): Observable<ValidationErrors> => {
      let errors$: Observable<ValidationErrors>;

      if (control.dirty && isNotBlank(control.value) && this.e164Pattern.test(control.value)) {
        this.evaluatingNumber = true;
        this.formControl.markAsPending();

        errors$ = timer(400).pipe(
          switchMap(() => this.sharedService.evaluateE164(control.value.replace('+', ''))),
          map((number) => {
            let errors: ValidationErrors;

            this.evaluatingNumber = false;

            if (this.typeIsAllowed(number)) {
              errors = null;

              this.evaluatedNumber = number;

              this.onChange(this.stringifyNumber(number));
            } else if (number.serviceType === NumberServiceType.UNKNOWN) {
              errors = {
                custom: 'numberEvaluator.validation.unrecognizedNumber',
              };

              this.evaluatedNumber = null;
              this.onChange(null);
            } else {
              errors = {
                custom: this.invalidMessage,
                customParameters: number,
              };

              this.evaluatedNumber = null;
              this.onChange(this.stringifyNumber(number));
            }

            this.updateErrors(errors);

            return errors;
          })
        );
      } else {
        errors$ = of(null);
      }

      return errors$;
    };
  }

  private typeIsAllowed(num: EvaluatedNumber): boolean {
    const isNotUnknown = num.serviceType !== NumberServiceType.UNKNOWN;
    const allServiceTypesAllowed = this.allowedServiceTypes.length === 0;
    const serviceTypeIsAllowed = this.allowedServiceTypes.includes(num.serviceType);

    const allNumberTypesAllowed = this.allowedNumberTypes.length === 0;
    const numberTypeIsAllowed = this.allowedNumberTypes.includes(num.numberType);

    return (
      isNotUnknown &&
      (allNumberTypesAllowed || numberTypeIsAllowed) &&
      (allServiceTypesAllowed || serviceTypeIsAllowed)
    );
  }

  private updateErrors(errors: ValidationErrors | null) {
    this.errors = errors;

    this.onValidatorChanged();
  }
}
